import { useTranslate } from '@tolgee/react';
import React from 'react';
import styled from 'styled-components';

import theme from '../../../assets/theme';
import useResponses from '../../../hooks/useResponses';
import useVotes from '../../../hooks/useVotes';
import { RoundState } from '../../../utils/roundUtils';
import { zIndexes } from '../../../utils/zIndexes';
import { IconWrapper } from '../../_atoms/IconWrapper';
import { Slide } from '../../SlideComponents/slide.types';

const ResponsecounterWidgetContainer = styled.div`
  display: flex;
  margin-bottom: 10px;
  color: ${theme.colors.white};
  width: max-content;
  font-size: 20px;
  font-weight: 600;
  padding: 10px;
  svg {
    color: ${theme.colors.white};
  }
  justify-content: space-evenly;
  align-items: center;
  background-color: ${theme.colors.translucentLightBlack};
  border-radius: 5px;
  z-index: ${zIndexes('LIVEGAME_TIMER_WIDGET')};
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
`;

const CounterViewer = ({
  countDescription,
  count,
}: {
  countDescription: string;
  count: number;
}) => {
  const { t } = useTranslate();
  const [displayCount, setDisplayCount] = React.useState(0);
  const previousCountRef = React.useRef(0);

  React.useEffect(() => {
    // Only animate if count increased
    if (count <= previousCountRef.current) {
      setDisplayCount(count);
      previousCountRef.current = count;
      return;
    }

    const startValue = previousCountRef.current;
    const endValue = count;
    const duration = 500; // 0.5 seconds
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-out cubic for that "blast through quickly then slow down" effect
      const easeOut = 1 - Math.pow(1 - progress, 3);

      const currentValue = Math.round(startValue + (endValue - startValue) * easeOut);
      setDisplayCount(currentValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        previousCountRef.current = endValue;
      }
    };

    requestAnimationFrame(animate);
  }, [count]);

  return (
    <ResponsecounterWidgetContainer>
      <IconWrapper
        height={`30px`}
        width={`30px`}
        style={{ cursor: 'default', marginRight: '5px' }}
      >
        {t(countDescription)}
      </IconWrapper>

      <span>
        {' '}
        <strong> {displayCount} </strong>
      </span>
    </ResponsecounterWidgetContainer>
  );
};

const VoteCounter = ({
  currentSlide,
  gameId,
  tenantId,
}: {
  gameId: string;
  tenantId: string;
  currentSlide: Slide;
}) => {
  const { t } = useTranslate();
  const { count } = useVotes(gameId, currentSlide.id, tenantId);
  return <CounterViewer count={count} countDescription={t('Votes:', 'Votes:')} />;
};

const WordCounter = ({
  currentSlide,
  gameId,
  tenantId,
}: {
  gameId: string;
  tenantId: string;
  currentSlide: Slide;
}) => {
  const { t } = useTranslate();
  const { wordCount } = useResponses(gameId, currentSlide.id, true, tenantId);
  return (
    <CounterViewer
      count={wordCount}
      countDescription={t('Words written:', 'Words written:')}
    />
  );
};
const DetailCounter = ({
  currentSlide,
  gameId,
  tenantId,
}: {
  gameId: string;
  tenantId: string;
  currentSlide: Slide;
}) => {
  const { t } = useTranslate();
  const { detailCount } = useResponses(gameId, currentSlide.id, true, tenantId);
  return (
    <CounterViewer
      count={detailCount}
      countDescription={t('Details added:', 'Details added:')}
    />
  );
};

const ResponseCounter = ({
  currentSlide,
  gameId,
  tenantId,
}: {
  gameId: string;
  tenantId: string;
  currentSlide: Slide;
}) => {
  const { t } = useTranslate();
  const { count } = useResponses(gameId, currentSlide.id, true, tenantId);
  return <CounterViewer count={count} countDescription={t('Responses:', 'Responses:')} />;
};
const LiveCounterWidget = ({
  currentSlide,
  gameId,
  roundState,
  tenantId,
}: {
  gameId: string;
  tenantId: string;
  currentSlide: Slide;
  roundState: RoundState;
}) => {
  if (
    (currentSlide.type === 'aiAssessedAnswers' ||
      currentSlide.type === 'answers' ||
      currentSlide.type === 'wordcloud') &&
    roundState === 'in'
  ) {
    return (
      <WordCounter currentSlide={currentSlide} gameId={gameId} tenantId={tenantId} />
    );
  }

  if (
    (currentSlide.type === 'aiAssessedImageAnswers' || currentSlide.type === 'canvas') &&
    roundState === 'in'
  ) {
    return (
      <DetailCounter currentSlide={currentSlide} gameId={gameId} tenantId={tenantId} />
    );
  }

  if (
    (currentSlide.type === 'poll' || currentSlide.type === 'multipleChoice') &&
    roundState === 'in'
  ) {
    return (
      <ResponseCounter currentSlide={currentSlide} gameId={gameId} tenantId={tenantId} />
    );
  }

  if (currentSlide.type === 'voting' && roundState === 'in') {
    return (
      <VoteCounter currentSlide={currentSlide} gameId={gameId} tenantId={tenantId} />
    );
  }

  return null;
};

export default React.memo(LiveCounterWidget);
